import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  Circle,
  Flex,
  Stack,
  Text,
} from '@chakra-ui/react';
import { useTranslation } from 'next-i18next';
import { useEffect, useRef, useState } from 'react';

import { REFS_MAP } from '$src/constants';
import { EXIT_INTENT_REASONS, type ExitIntentReason } from '$src/hooks/client/useExitIntent';

/**
 * Fisher-Yates shuffle algorithm
 */
function shuffleArray<T>(array: readonly T[]): T[] {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const temp = shuffled[i]!;
    shuffled[i] = shuffled[j]!;
    shuffled[j] = temp;
  }
  return shuffled;
}

type ExitIntentPopupProps = {
  isOpen: boolean;
  onLeave: () => void;
  onReasonSelected: (reason: ExitIntentReason) => void | Promise<void>;
  /** If true, shuffle all options except the last one ("Other") */
  shuffleOptions?: boolean;
};

const ExitIntentPopup = ({
  isOpen,
  onLeave,
  onReasonSelected,
  shuffleOptions = false,
}: ExitIntentPopupProps) => {
  const { t } = useTranslation();
  const cancelRef = useRef(null);

  // Shuffle all options except the last one when the popup opens
  const [displayReasons, setDisplayReasons] = useState<readonly ExitIntentReason[]>(EXIT_INTENT_REASONS);

  useEffect(() => {
    if (isOpen) {
      if (shuffleOptions) {
        // Keep the last option ("Other") at the end, shuffle the rest
        const lastOption = EXIT_INTENT_REASONS[EXIT_INTENT_REASONS.length - 1]!;
        const optionsToShuffle = EXIT_INTENT_REASONS.slice(0, -1);
        setDisplayReasons([...shuffleArray(optionsToShuffle), lastOption]);
      } else {
        setDisplayReasons(EXIT_INTENT_REASONS);
      }
    }
  }, [isOpen, shuffleOptions]);

  return (
    <AlertDialog
      portalProps={{
        containerRef: REFS_MAP.get('portal') as any,
      }}
      motionPreset="slideInBottom"
      leastDestructiveRef={cancelRef as any}
      onClose={onLeave}
      isOpen={isOpen}
      isCentered
    >
      <AlertDialogOverlay />

      <AlertDialogContent
        mx={4}
        borderRadius="1.25rem"
        boxShadow="var(--toast-shadow)"
        border="1px solid var(--border)"
        outline="none"
        bg="var(--card-bg)"
        backdropFilter="blur(20px)"
      >
        <AlertDialogHeader
          marginTop={10}
          fontSize="1.125rem"
          pt="1"
          pb="1"
          textAlign="center"
          fontWeight="600"
          color="var(--text)"
        >
          {t('exitIntent.title')}
        </AlertDialogHeader>
        <AlertDialogBody pb="4">
          <Text fontSize="0.875rem" color="var(--text-secondary)" textAlign="center" mb="4">
            {t('exitIntent.subtitle')}
          </Text>
          <Stack spacing={3}>
            {displayReasons.map((reason) => (
              <Flex
                key={reason}
                as="button"
                type="button"
                align="center"
                gap={3}
                py={2}
                px={1}
                cursor="pointer"
                borderRadius="md"
                _hover={{ bg: 'var(--bg-secondary)' }}
                onClick={() => onReasonSelected(reason)}
                data-ph-capture-attribute-tracking-id={`exit-intent-reason-${reason}`}
              >
                <Circle size="18px" border="2px solid" borderColor="var(--text-muted)" />
                <Text fontSize="0.875rem" color="var(--text)">
                  {t(`exitIntent.reasons.${reason}`)}
                </Text>
              </Flex>
            ))}
          </Stack>
        </AlertDialogBody>
        <AlertDialogFooter pt="0">
          <Stack width="100%" spacing={0}>
            <Button
              ref={cancelRef as any}
              variant="ghost"
              w="full"
              py="3"
              fontWeight="400"
              color="var(--text-secondary)"
              onClick={onLeave}
              data-ph-capture-attribute-tracking-id="exit-intent-leave"
            >
              {t('exitIntent.leave')}
            </Button>
          </Stack>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default ExitIntentPopup;
